import { ReactNode } from "react";
import { IPositionQueue, IQueue, IUserQueue, IUserQueueWithFullName, IUserWithQueue } from "@/Settings/types/QueueMapping/QueueMapping";

// MUI
import { Grid, Typography } from "@mui/material";

// Layouts
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";

interface IUsersMultiSelectProps {
	users: IUserWithQueue[];
	usersType: string;
	queue?: IQueue;
	uniqueUsers: string[] | ReactNode;
	loading?: boolean;
	disabled?: boolean;
	footer?: ReactNode;
	onUserChange: (selected: IUserWithQueue[]) => void;
	error: boolean;
}

const findUser = (users: IUserWithQueue[], id: string): IUserWithQueue | undefined => {
	return users.find((user: IUserWithQueue) => user._id === id);
};

const transformSelectedUsers = (users: IUserQueue[], associates: IUserWithQueue[], positionName: string): IUserQueueWithFullName[] => {
	const transformedUsers: IUserQueueWithFullName[] = [];
	users.forEach((user: IUserQueue) => {
		const userFound = findUser(associates, user._id);
		if (userFound) {
			transformedUsers.push({
				_id: user._id,
				name: user.name,
				fullName: user.name,
				position: positionName,
			} as IUserQueueWithFullName);
		}
	});

	return transformedUsers;
};

//Simple mapping for add `fullName` property that it's the one that all users has
const getSelectedUsers = (queue: IQueue | undefined, users: IUserWithQueue[]): IUserQueueWithFullName[] => {
	if (!queue?.usersByPosition?.length) {
		return [];
	}

	let selectedUsers: IUserQueueWithFullName[] = [];
	queue.usersByPosition.forEach((pos: IPositionQueue) => {
		if (!pos.users?.length) {
			return;
		}
		const transformedUsers = transformSelectedUsers(pos.users, users, pos.name);
		selectedUsers = [...selectedUsers, ...transformedUsers];
	});

	return selectedUsers;
};

function UsersMultiSelect(props: Readonly<IUsersMultiSelectProps>) {
	const { queue, users, usersType, uniqueUsers, loading, disabled, onUserChange, footer, error } = props;

	const usersWithQueueStatus = () => {
		if (users.length === 0) {
			return [];
		}
		// Mapping for mark with `*` the users that have queue marked in their call activity pay plan.
		return users.map((user: IUserWithQueue) => {
			if (!Array.isArray(uniqueUsers)) {
				return user;
			}
			const isNotIncluded = !uniqueUsers?.includes(user.fullName) ? "**" : "*";

			return {
				...user,
				fullName: user.hasQueue ? `${user.fullName} ${isNotIncluded}` : user.fullName,
			};
		});
	};

	const handleUserSelectChange = (selected: IUserQueueWithFullName[]) => {
		const transformedUsers: IUserWithQueue[] = selected.map((user: IUserQueueWithFullName) => {
			const fullName = typeof user.fullName === "object" ? (user.fullName as React.ReactElement).props.children[0] : user.fullName;
			return {
				_id: user._id,
				position: user?.position ?? "",
				hasQueue: "",
				employeeId: "",
				fullName,
			};
		});
		onUserChange(transformedUsers);
	};

	return (
		<Grid item container direction={"column"} rowSpacing={2}>
			<Grid item>
				<Typography className="regular-montserrat-label fs-18">{usersType}</Typography>
			</Grid>
			<Grid item>
				<SmartSelector
					classes="!w-[253px]"
					multiple
					multipleSelectAll
					tagsVariant={"text"}
					showBy={"fullName"}
					options={usersWithQueueStatus()}
					selected={getSelectedUsers(queue, users)}
					disabledOptions={queue?.usersByPosition
						.map((pos: IPositionQueue) => pos.users.map((user: IUserQueue) => user._id))
						.flat()}
					groupingProps={{
						groupBy: "position",
						sticky: true,
						classes: "!text-primary !font-bold !font-montserrat",
					}}
					inputProps={{
						placeholder: "Select an User",
						hideTooltip: true,
					}}
					listBoxProps={{
						sx: { maxHeight: "220px" },
					}}
					disabled={disabled}
					error={!disabled && error}
					hideTooltip={true}
					loading={loading}
					onChangeAction={(values) => handleUserSelectChange(values as IUserQueueWithFullName[])}
				/>
			</Grid>
			{footer && <>{footer}</>}
		</Grid>
	);
}

export default UsersMultiSelect;
